import type { WeekHours } from '@pulsify/shared/dto'
import { Plus, X } from 'lucide-react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Input } from '~/shared/ui/input'
import { type HoursRange, WEEK_ORDER, type WeekdayKey } from './map-place'

function dayLabels(): Record<WeekdayKey, string> {
  return {
    '0': m.placeWebDaySun(),
    '1': m.placeWebDayMon(),
    '2': m.placeWebDayTue(),
    '3': m.placeWebDayWed(),
    '4': m.placeWebDayThu(),
    '5': m.placeWebDayFri(),
    '6': m.placeWebDaySat(),
  }
}

const DEFAULT_RANGE: HoursRange = { open: '09:00', close: '18:00' }

interface HoursRowsProps {
  value: WeekHours
  onChange: (next: WeekHours) => void
}

export function HoursRows({ value, onChange }: HoursRowsProps) {
  const labels = dayLabels()
  const update = (day: WeekdayKey, ranges: HoursRange[]) => onChange({ ...value, [day]: ranges })

  return (
    <div className="divide-y divide-border/60">
      {WEEK_ORDER.map((day) => {
        const ranges = value[day] ?? []
        const last = ranges[ranges.length - 1]
        const nextRange: HoursRange = last ? { open: last.close, close: '23:00' } : DEFAULT_RANGE
        const setRange = (index: number, patch: Partial<HoursRange>) =>
          update(
            day,
            ranges.map((range, i) => (i === index ? { ...range, ...patch } : range)),
          )
        return (
          <div key={day} className="flex items-start gap-3 py-2">
            <span
              className={cn(
                'w-8 shrink-0 pt-2 font-medium text-sm tabular-nums',
                ranges.length === 0 ? 'text-muted-foreground' : 'text-foreground',
              )}
            >
              {labels[day]}
            </span>
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              {ranges.length === 0 && (
                <span className="pt-2 text-muted-foreground text-sm">{m.placeWebClosed()}</span>
              )}
              {ranges.map((range, index) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: ranges have no identity beyond position
                  key={index}
                  className="flex items-center gap-1.5"
                >
                  <Input
                    type="time"
                    step={300}
                    value={range.open}
                    aria-label={m.placeWebOpens({ day: labels[day] })}
                    onChange={(event) => setRange(index, { open: event.target.value })}
                    className="h-8 w-[6.5rem] px-2 tabular-nums md:text-sm"
                  />
                  <span className="text-muted-foreground">–</span>
                  <Input
                    type="time"
                    step={300}
                    value={range.close}
                    aria-label={m.placeWebCloses({ day: labels[day] })}
                    onChange={(event) => setRange(index, { close: event.target.value })}
                    className="h-8 w-[6.5rem] px-2 tabular-nums md:text-sm"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={m.placeWebRemoveRange()}
                    onClick={() =>
                      update(
                        day,
                        ranges.filter((_, i) => i !== index),
                      )
                    }
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={m.placeWebAddRange()}
                onClick={() => update(day, [...ranges, nextRange])}
                className="text-muted-foreground hover:text-foreground"
              >
                <Plus />
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
